import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SEED_LESSONS } from "../data/seed-lessons";
import { useLocalProfile } from "../local/useLocalProfile";
import { AvatarPreview } from "../components/AvatarEditor";
import { MainMenuHero } from "../components/MainMenuHero";
import { AuthModal } from "../components/AuthModal";
import { SeasonProgress } from "../components/SeasonProgress";
import { createGuestProfile, isGuest } from "../local/guestUser";
import { isLessonFinished, isLessonUnlocked } from "../local/profile";
import { getActiveSession, clearSession } from "../local/session";
import { enableMenuMusicOnUserAction, unlockAudioOnUserGesture } from "../game/audio";

/**
 * Main menu. Standard game shape:
 *   ▶ Play        → level select
 *   ⚔ Battle      → multiplayer race lobby
 *   ⚙ Settings    → name / avatar / reset
 *
 * If no character exists yet, the only action is "Make character" → /create.
 */
export function LocalLanding() {
  const { profile, setProfile } = useLocalProfile();
  const nav = useNavigate();
  const [modeOpen, setModeOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [seasonOpen, setSeasonOpen] = useState(false);

  // First-time visitors land directly on the menu as a guest. No more
  // forced "Create player" gate. They can sign in or convert later via
  // the Sign In button.
  useEffect(() => {
    if (!profile) {
      setProfile(createGuestProfile());
    }
  }, [profile, setProfile]);

  // Lock body scroll only while the lobby is mounted. Other pages scroll.
  useEffect(() => {
    document.body.classList.add("wr-lobby-active");
    return () => document.body.classList.remove("wr-lobby-active");
  }, []);
  const lessons = [...SEED_LESSONS].sort((a, b) => a.orderIndex - b.orderIndex);

  // First Play tap is the user-gesture that unlocks menu music. From this
  // point on, music behaves normally.
  const openModeDialog = () => {
    enableMenuMusicOnUserAction();
    // Resume Web Audio contexts here so SFX + voice are ready by the
    // time the countdown starts. Previously the first audible event
    // was the question-gate prompt (which silently kicked the unlock
    // retry path), so everything before that was muted.
    unlockAudioOnUserGesture();
    setModeOpen(true);
  };

  const [isFs, setIsFs] = useState(typeof document !== "undefined" && !!document.fullscreenElement);
  useEffect(() => {
    const doc = document as Document & { webkitFullscreenElement?: Element };
    const onFs = () => setIsFs(!!(document.fullscreenElement || doc.webkitFullscreenElement));
    document.addEventListener("fullscreenchange", onFs);
    document.addEventListener("webkitfullscreenchange", onFs as EventListener);
    return () => {
      document.removeEventListener("fullscreenchange", onFs);
      document.removeEventListener("webkitfullscreenchange", onFs as EventListener);
    };
  }, []);
  const [fsHint, setFsHint] = useState(false);

  // Resume the previous run if the player refreshed, locked the iPad, or
  // pressed home during a lesson / battle. Only fires ONCE per tab session
  // (refresh resets the gate, normal in-app navigation does not) so the
  // player can deliberately go back to the menu without being yanked into
  // the previous run. MUST stay above the `if (!profile)` early return so the
  // hook order is stable when the auto-guest profile materialises (React #310).
  useEffect(() => {
    if (sessionStorage.getItem("wr_resumed_v1")) return;
    sessionStorage.setItem("wr_resumed_v1", "1");
    const s = getActiveSession();
    if (!s) return;
    clearSession();
    if (s.soloLessonId) {
      nav(`/play/lesson/${s.soloLessonId}`, { replace: true });
    } else if (s.battle) {
      const q = s.battle.asHost ? `?code=${s.battle.code}&host=1` : `?code=${s.battle.code}`;
      nav(`/battle${q}`, { replace: true });
    }
  }, [nav]);

  const toggleFullscreen = async () => {
    const doc = document as Document & {
      webkitFullscreenElement?: Element;
      webkitExitFullscreen?: () => Promise<void>;
    };
    const el = document.documentElement as HTMLElement & {
      webkitRequestFullscreen?: () => Promise<void>;
    };
    const hasFsApi =
      typeof el.requestFullscreen === "function" ||
      typeof el.webkitRequestFullscreen === "function";
    if (!hasFsApi) {
      // iPhone Safari blocks the Fullscreen API completely. Show a one-
      // shot tip telling the user how to get fullscreen via the home
      // screen install flow, which DOES launch chrome-less via the
      // manifest's `"display": "fullscreen"`.
      setFsHint(true);
      setTimeout(() => setFsHint(false), 4500);
      return;
    }
    try {
      const isFsNow = !!(document.fullscreenElement || doc.webkitFullscreenElement);
      if (isFsNow) {
        await (document.exitFullscreen?.bind(document)
          ?? doc.webkitExitFullscreen?.bind(doc))?.();
      } else {
        await (el.requestFullscreen?.bind(el)
          ?? el.webkitRequestFullscreen?.bind(el))?.();
      }
    } catch {
      // Request rejected (PWA standalone mode, browser policy, iframed,
      // etc.). Show the same tip so the player has feedback instead of
      // a silent no-op.
      setFsHint(true);
      setTimeout(() => setFsHint(false), 4500);
    }
  };

  // While the auto-guest profile is materialising for the first time, show
  // a quick splash so we never render the menu with `profile === null`.
  if (!profile) {
    return (
      <div className="shell-narrow stack-loose" style={{ paddingTop: "10vh" }}>
        <div className="t-center">
          <h1 className="t-display">Word Runner</h1>
          <p className="t-body t-muted">Loading…</p>
        </div>
      </div>
    );
  }
  const guestMode = isGuest(profile);

  const unlockedLessons = lessons.filter((lesson) => isLessonUnlocked(profile, lesson.orderIndex, lessons));
  const currentLesson =
    lessons.find(
      (lesson) =>
        isLessonUnlocked(profile, lesson.orderIndex, lessons) &&
        !isLessonFinished(profile, lesson.id),
    ) ??
    unlockedLessons[unlockedLessons.length - 1] ??
    lessons[0]!;
  const playerLevel = Math.max(1, currentLesson.orderIndex);
  const totalStars = lessons.reduce(
    (sum, lesson) => sum + lesson.words.filter((w) => profile.mastery[w.id]?.timesCorrect).length,
    0,
  );
  const xpGoal = 800;
  const xpNow = Math.min(xpGoal, (profile.coins * 8 + totalStars * 30) % xpGoal);
  const xpPct = Math.max(8, Math.round((xpNow / xpGoal) * 100));

  const levelsCompleted = lessons.filter((l) => isLessonFinished(profile, l.id)).length;
  const levelsTotal = 80;

  return (
    <div className="home-dashboard home-lobby home-lobby-v2 lobby-mount-in">
      {/* Big logo top-left */}
      <div className="lobby-logo" aria-hidden>
        <h1>WORD<br />RUNNER</h1>
      </div>

      {/* Top-right HUD cluster (fullscreen / settings / profile).
       *  Fullscreen button is shown on every device. On iPhone Safari
       *  (which doesn't expose the Fullscreen API) tapping shows a
       *  tip telling the player to Add to Home Screen for chrome-less
       *  play instead. */}
      <div className="lobby-hud-row lobby-hud-v2">
        <button
          className="lobby-hud-btn"
          onClick={toggleFullscreen}
          aria-label={isFs ? "Exit fullscreen" : "Enter fullscreen"}
        >{isFs ? "⤓" : "⛶"}</button>
        <button
          className="lobby-hud-btn"
          onClick={() => nav("/settings")}
          aria-label="Settings"
        >⚙</button>
        <button
          className="lobby-profile-chip"
          onClick={() => (guestMode ? setAuthOpen(true) : nav("/character"))}
        >
          <span className="lobby-profile-face">
            {guestMode ? "👤" : <AvatarPreview cfg={profile.avatarConfig} />}
          </span>
          <span className="lobby-profile-meta">
            <strong>{guestMode ? "Profile" : profile.displayName}</strong>
            <span>{guestMode ? "Sign in" : `Level ${playerLevel}`}</span>
          </span>
        </button>
      </div>

      {/* Left sidebar: season chip, season progress, nav pills */}
      <aside className="lobby-side lobby-side-left">
        <button className="lobby-season-chip" onClick={() => nav("/season")}><span aria-hidden>⭐</span> Season 1</button>

        <div
          className="lobby-progress-card"
          role="button"
          tabIndex={0}
          onClick={() => nav("/season")}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") nav("/season"); }}
        >
          <div className="lobby-progress-label">SEASON PROGRESS</div>
          <div className="lobby-progress-row">
            <span className="lobby-progress-star" aria-hidden>⭐</span>
            <div className="lobby-progress-num">
              <strong>{levelsCompleted} / {levelsTotal}</strong>
              <small>levels completed</small>
            </div>
          </div>
          <div className="lobby-progress-bar">
            <i style={{ width: `${Math.max(4, (levelsCompleted / levelsTotal) * 100)}%` }} />
          </div>
        </div>

        <nav className="lobby-nav">
          <button
            className="lobby-nav-pill"
            onClick={() => (guestMode ? setAuthOpen(true) : nav("/character"))}
          >
            <span className="lobby-nav-icon nav-icon-profile" aria-hidden>👤</span>
            <span><strong>Profile</strong><small>Customize your look</small></span>
          </button>
          <button className="lobby-nav-pill" onClick={() => setSeasonOpen(true)}>
            <span className="lobby-nav-icon nav-icon-stats" aria-hidden>📊</span>
            <span><strong>Stats</strong><small>Track your progress</small></span>
          </button>
        </nav>
      </aside>

      {/* Centre stage: 3D hero + big PLAY button */}
      <section className="lobby-stage-v2">
        <div className="lobby-stage-canvas">
          <MainMenuHero
            avatar={profile.avatarConfig}
            theme={currentLesson.theme}
            timeOfDay={currentLesson.timeOfDay}
          />
        </div>
        <button className="lobby-play-v2" onClick={openModeDialog}>
          <span aria-hidden>▶</span>
          PLAY
        </button>
      </section>

      {/* Right sidebar: NEW level promo, coin/level/XP cards, achievements */}
      <aside className="lobby-side lobby-side-right">
        <button
          className="lobby-promo-card lobby-promo-card-image"
          onClick={() => nav("/play")}
          aria-label="Spaceship Run — Try Now"
        >
          <span className="lobby-promo-tag">NEW</span>
          <span className="lobby-promo-bigtitle">SPACESHIP<br />RUN</span>
          <span className="lobby-promo-blurb">Jump, dodge<br />and <em>learn!</em></span>
          <span className="lobby-promo-cta">Try Now! <span aria-hidden>›</span></span>
        </button>
        <div className="lobby-stats">
          <div className="lobby-stat-card">
            <span className="lobby-stat-icon lobby-stat-coin" aria-hidden>●</span>
            <span className="lobby-stat-meta">
              <strong>{profile.coins}</strong>
              <small>Coins</small>
            </span>
          </div>
          <div className="lobby-stat-card">
            <span className="lobby-stat-icon lobby-stat-level" aria-hidden>◆</span>
            <span className="lobby-stat-meta">
              <strong>{playerLevel}</strong>
              <small>Level</small>
            </span>
          </div>
        </div>
        <div className="lobby-xp-card">
          <span className="lobby-xp-badge">{playerLevel}</span>
          <div className="lobby-xp-meta">
            <strong>{Math.max(0, xpGoal - xpNow)} XP to level up</strong>
            <div className="lobby-xp-bar"><i style={{ width: `${xpPct}%` }} /></div>
          </div>
        </div>
        <button className="lobby-pill lobby-pill-achievements" onClick={() => setSeasonOpen(true)}>
          <span aria-hidden>🏆</span> Achievements
        </button>
      </aside>

      {authOpen && <AuthModal onClose={() => setAuthOpen(false)} />}
      {seasonOpen && (
        <SeasonProgress
          currentRuns={profile.lifetimeRuns ?? 0}
          onClose={() => setSeasonOpen(false)}
        />
      )}

      {modeOpen && (
        <div className="mode-v2-overlay" role="dialog" aria-modal="true" aria-label="Choose mode" onClick={() => setModeOpen(false)}>
          <div className="mode-v2-card" onClick={(e) => e.stopPropagation()}>
            <button className="popup-close-x" onClick={() => setModeOpen(false)} aria-label="Close">×</button>
            <h2 className="mode-v2-title">Choose Mode</h2>
            <div className="mode-v2-list">
              <button className="mode-v2-row mode-v2-solo" onClick={() => nav("/play")}>
                <span className="mode-v2-icon" aria-hidden>▶</span>
                <span className="mode-v2-text">
                  <strong>Solo Run</strong>
                  <small>Practice by yourself</small>
                </span>
                <span className="mode-v2-art" aria-hidden>🏃</span>
              </button>
              <button className="mode-v2-row mode-v2-battle" onClick={() => nav("/battle")}>
                <span className="mode-v2-icon" aria-hidden>⚔</span>
                <span className="mode-v2-text">
                  <strong>Battle</strong>
                  <small>Race other players</small>
                </span>
                <span className="mode-v2-art" aria-hidden>🥷</span>
              </button>
              <button className="mode-v2-row mode-v2-quizzle" onClick={() => nav("/quizzle")}>
                <span className="mode-v2-icon" aria-hidden>🧩</span>
                <span className="mode-v2-text">
                  <strong>Quizzle</strong>
                  <small>Class quiz — host screen + iPad controllers</small>
                </span>
                <span className="mode-v2-art" aria-hidden>📺</span>
              </button>
              <button className="mode-v2-row mode-v2-class" onClick={() => nav("/class")}>
                <span className="mode-v2-icon" aria-hidden>🎓</span>
                <span className="mode-v2-text">
                  <strong>Class</strong>
                  <small>Join your class — teacher runs the session</small>
                </span>
                <span className="mode-v2-art" aria-hidden>🧑‍🏫</span>
              </button>
              <button className="mode-v2-row mode-v2-teacher" onClick={() => nav("/teacher")}>
                <span className="mode-v2-icon" aria-hidden>🎓</span>
                <span className="mode-v2-text">
                  <strong>Teacher Mode</strong>
                  <small>Play with your class</small>
                </span>
                <span className="mode-v2-art" aria-hidden>👩‍🏫</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {fsHint && (
        <div className="fs-hint-toast" role="status" aria-live="polite">
          <strong>Fullscreen on iPhone</strong>
          <span>iOS Safari blocks fullscreen. Tap the <b>Share</b> icon → <b>Add to Home Screen</b> to launch Word Runner without the browser bars.</span>
        </div>
      )}

    </div>
  );
}
